import React, { useState, useEffect } from "react";
import { Button } from "@/shared/components/ui/button";
import { Switch } from "@/shared/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Sheet,
  SheetContent,
} from "@/shared/components/ui/sheet";
import {
  Building2, Key, Settings, Copy, Eye, EyeOff,
  CheckCircle2, Save, Info, Sparkles, Lock,
  AlertCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import * as resellerService from "@/shared/api/services/resellerService";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

function generateApiKey() {
  return "pk_live_" + Array.from({ length: 32 }, () =>
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]
  ).join("");
}
function generateSecretKey() {
  return "sk_live_" + Array.from({ length: 40 }, () =>
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]
  ).join("");
}

/* ─── THEMED INPUT ─── */
function ThemedInput({ label, icon: Icon, hint, disabled, style: extraStyle, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      {label && (
        <label style={{
          fontSize: "10px",
          fontWeight: 700,
          color: focused ? "#06b6d4" : "#475569",
          textTransform: "uppercase",
          letterSpacing: "0.12em",
          transition: "color 0.2s",
          fontFamily: "'Sora', sans-serif",
        }}>
          {label}
        </label>
      )}
      <div style={{ position: "relative" }}>
        {Icon && (
          <Icon style={{
            position: "absolute", left: "11px", top: "50%", transform: "translateY(-50%)",
            width: "13px", height: "13px",
            color: focused ? "#06b6d4" : "#2a3a52",
            transition: "color 0.2s", zIndex: 1
          }} />
        )}
        <input
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={disabled}
          autoComplete="new-password"
          {...props}
          style={{
            width: "100%",
            padding: Icon ? "10px 12px 10px 34px" : "10px 12px",
            borderRadius: "10px",
            background: disabled ? "rgba(6,12,26,0.5)" : focused ? "rgba(6,12,26,0.95)" : "rgba(6,12,26,0.85)",
            border: focused
              ? "1px solid rgba(6,182,212,0.4)"
              : "1px solid rgba(255,255,255,0.07)",
            color: disabled ? "#334155" : "#c8dff0",
            fontSize: "13px",
            fontFamily: "'Sora', sans-serif",
            outline: "none",
            transition: "all 0.25s ease",
            boxShadow: focused
              ? "0 0 0 3px rgba(6,182,212,0.07), inset 0 1px 0 rgba(6,182,212,0.06)"
              : "inset 0 1px 0 rgba(255,255,255,0.02)",
            cursor: disabled ? "not-allowed" : "text",
            opacity: disabled ? 0.6 : 1,
            WebkitTextFillColor: disabled ? "#334155" : "#c8dff0",
            ...extraStyle,
          }}
        />
        {focused && (
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            style={{
              position: "absolute", bottom: 0, left: "10px", right: "10px",
              height: "1.5px", borderRadius: "0 0 10px 10px",
              background: "linear-gradient(90deg, transparent, #06b6d4, transparent)",
              transformOrigin: "left",
            }}
          />
        )}
      </div>
      {hint && <p style={{ fontSize: "10px", color: "#2a3a52", marginTop: "2px", fontFamily: "'Sora', sans-serif" }}>{hint}</p>}
    </div>
  );
}

/* ─── THEMED SELECT ─── */
function ThemedSelect({ label, value, onValueChange, options, disabled }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      {label && (
        <label style={{
          fontSize: "10px", fontWeight: 700, color: "#475569",
          textTransform: "uppercase", letterSpacing: "0.12em",
          fontFamily: "'Sora', sans-serif",
        }}>
          {label}
        </label>
      )}
      <Select value={value} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger style={{
          background: disabled ? "rgba(6,12,26,0.5)" : "rgba(6,12,26,0.85)",
          border: "1px solid rgba(255,255,255,0.07)",
          borderRadius: "10px",
          color: "#c8dff0",
          fontSize: "13px",
          fontFamily: "'Sora', sans-serif",
          height: "40px",
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "border 0.2s ease",
          outline: "none",
        }}
          onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.borderColor = "rgba(6,182,212,0.25)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; }}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent style={{ background: "#070f1d", border: "1px solid rgba(6,182,212,0.15)", borderRadius: "10px" }}>
          {options.map(({ value: v, label: l }) => (
            <SelectItem key={v} value={v} style={{ color: "#c8dff0", fontSize: "13px", fontFamily: "'Sora', sans-serif" }}>{l}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/* ─── TOGGLE CARD ─── */
function ToggleCard({ label, description, checked, onChange }) {
  return (
    <motion.div
      whileHover={{ scale: 1.005 }}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "13px 15px",
        borderRadius: "12px",
        background: checked ? "rgba(6,182,212,0.05)" : "rgba(6,12,26,0.7)",
        border: checked ? "1px solid rgba(6,182,212,0.18)" : "1px solid rgba(255,255,255,0.05)",
        transition: "all 0.25s ease",
        cursor: "pointer",
      }}
      onClick={() => onChange(!checked)}
    >
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: "12.5px", fontWeight: 700, color: checked ? "#c8dff0" : "#64748b", margin: 0, fontFamily: "'Sora', sans-serif" }}>{label}</p>
        <p style={{ fontSize: "11px", color: "#2a3a52", margin: "3px 0 0", fontFamily: "'Sora', sans-serif" }}>{description}</p>
      </div>
      <div onClick={(e) => { e.stopPropagation(); onChange(!checked); }}>
        <Switch checked={checked} onCheckedChange={onChange} />
      </div>
    </motion.div>
  );
}

export default function ResellerMerchantForm({ open, onClose, merchant, onSave, saving }) {
  const { t } = useLanguage();
  const isEdit = !!merchant;

  const { data: resellerProfile } = useQuery({
    queryKey: ["reseller-profile"],
    queryFn: () => resellerService.getProfile(),
    enabled: open,
  });

  const [formData, setFormData] = useState({
    name: "", email: "", password: "", status: "ACTIVE", code: "",
    api_key: generateApiKey(), secret_key: generateSecretKey(),
    callback_url: "", webhook_url: "",
    deposit_enabled: true, withdrawal_enabled: true,
    min_deposit: 100, max_deposit: 1000000,
    min_withdrawal: 100, max_withdrawal: 500000,
    fee_type: "percentage", fee_value: "",
    settlement_period: "T1",
    contact_name: "", contact_email: "", contact_phone: "",
    notes: "",
  });

  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [copied, setCopied] = useState(null);
  const [activeTab, setActiveTab] = useState("general");

  useEffect(() => {
    if (merchant) {
      setFormData(prev => ({
        ...prev, ...merchant, password: "",
        fee_value: merchant.depositCommission ?? 2.5,
        fee_type: "percentage",
      }));
    } else {
      setFormData({
        name: "", email: "", password: "", status: "ACTIVE", code: "",
        api_key: generateApiKey(), secret_key: generateSecretKey(),
        callback_url: "", webhook_url: "",
        deposit_enabled: true, withdrawal_enabled: true,
        min_deposit: 100, max_deposit: 1000000,
        min_withdrawal: 100, max_withdrawal: 500000,
        fee_type: "percentage", fee_value: resellerProfile?.depositCommission ?? 2.5,
        settlement_period: "T1",
        contact_name: "", contact_email: "", contact_phone: "",
        notes: "",
      });
    }
    setActiveTab("general");
  }, [merchant, open, resellerProfile]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleSubmit = () => {
    if (merchant) {
      onSave({ name: formData.name, status: formData.status });
    } else {
      onSave({
        email: formData.email, password: formData.password, name: formData.name,
        apiKey: formData.api_key || undefined, secretKey: formData.secret_key || undefined,
        depositCommission: formData.fee_value && formData.fee_type === "percentage" ? formData.fee_value : undefined,
        percentageFee: formData.fee_value && formData.fee_type === "percentage" ? formData.fee_value : undefined,
        webhook: formData.webhook_url || undefined, callback: formData.callback_url || undefined,
      });
    }
  };

  const tabs = [
    { id: "general", label: t("general"), icon: Building2 },
    { id: "settings", label: t("settings"), icon: Settings },
    { id: "api", label: t("apiKeys"), icon: Key },
  ];

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

    .merchant-sheet-content {
      background: #060d1a !important;
      border-left: 1px solid rgba(6,182,212,0.1) !important;
      font-family: 'Sora', sans-serif !important;
    }
    .merchant-sheet-content * { font-family: 'Sora', sans-serif; }

    .form-scrollable {
      overflow-y: auto;
      scrollbar-width: thin;
      scrollbar-color: rgba(6,182,212,0.15) transparent;
    }
    .form-scrollable::-webkit-scrollbar { width: 3px; }
    .form-scrollable::-webkit-scrollbar-track { background: transparent; }
    .form-scrollable::-webkit-scrollbar-thumb { background: rgba(6,182,212,0.18); border-radius: 3px; }

    /* Force dark autofill background */
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 1000px #060d1a inset !important;
      -webkit-text-fill-color: #c8dff0 !important;
      background-color: #060d1a !important;
      border-color: rgba(255,255,255,0.07) !important;
      caret-color: #c8dff0 !important;
      transition: background-color 9999s ease-in-out 0s !important;
    }

    /* For disabled autofill */
    input:disabled:-webkit-autofill {
      -webkit-text-fill-color: #334155 !important;
    }

    .tab-btn {
      display: flex; align-items: center; gap: 6px;
      padding: 8px 13px;
      border-radius: 10px;
      border: 1px solid transparent;
      font-size: 11.5px; font-weight: 600;
      cursor: pointer;
      transition: all 0.25s ease;
      background: transparent;
      color: #334155;
      flex: 1;
      justify-content: center;
      white-space: nowrap;
      font-family: 'Sora', sans-serif;
    }
    .tab-btn.active {
      background: rgba(6,182,212,0.1);
      border: 1px solid rgba(6,182,212,0.25);
      color: #06b6d4;
      box-shadow: 0 2px 14px rgba(6,182,212,0.1);
    }
    .tab-btn:not(.active):hover {
      background: rgba(255,255,255,0.03);
      color: #64748b;
      border-color: rgba(255,255,255,0.05);
    }

    .key-box {
      padding: 11px 13px;
      background: rgba(3,6,14,0.95);
      border: 1px solid rgba(6,182,212,0.1);
      border-radius: 10px;
      font-family: 'JetBrains Mono', monospace;
      font-size: 11px;
      color: #38bdf8;
      overflow-x: auto;
      flex: 1;
      white-space: nowrap;
    }

    .icon-btn {
      width: "34px"; height: "34px";
      border-radius: 9px;
      border: 1px solid rgba(6,182,212,0.15);
      background: rgba(6,182,212,0.05);
      color: #334155;
      cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      transition: all 0.22s ease;
      flex-shrink: 0;
      outline: none;
    }
    .icon-btn:hover {
      background: rgba(6,182,212,0.12);
      border-color: rgba(6,182,212,0.35);
      color: #06b6d4;
    }

    .section-divider {
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(6,182,212,0.12), transparent);
      margin: 4px 0;
    }

    textarea.themed {
      width: 100%;
      padding: 10px 12px;
      border-radius: 10px;
      background: rgba(6,12,26,0.85);
      border: 1px solid rgba(255,255,255,0.07);
      color: #c8dff0;
      font-size: 13px;
      font-family: 'Sora', sans-serif;
      outline: none;
      transition: all 0.25s ease;
      resize: vertical;
    }
    textarea.themed:focus {
      border-color: rgba(6,182,212,0.4);
      background: rgba(6,12,26,0.95);
      box-shadow: 0 0 0 3px rgba(6,182,212,0.06);
    }

    .label-sm {
      font-size: 10px;
      font-weight: 700;
      color: #475569;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      display: block;
      margin-bottom: 6px;
      font-family: 'Sora', sans-serif;
    }

    .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 13px; }
    .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 11px; }
    .flex-col { display: flex; flex-direction: column; gap: 8px; }
    .gap-16 { gap: 16px; }
  `;

  return (
    <>
      <style>{css}</style>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent
          className="merchant-sheet-content w-full sm:max-w-xl flex flex-col p-0"
          style={{ maxHeight: "100vh" }}
        >
          {/* Header */}
          <div style={{
            padding: "20px 22px 16px",
            borderBottom: "1px solid rgba(255,255,255,0.04)",
            background: "linear-gradient(135deg, rgba(6,182,212,0.04) 0%, transparent 100%)",
            flexShrink: 0,
          }}>
            {/* Top shimmer line */}
            <div style={{
              position: "absolute", top: 0, left: "20%", right: "20%", height: "1px",
              background: "linear-gradient(90deg, transparent, rgba(6,182,212,0.25), transparent)",
            }} />
            <div style={{ display: "flex", alignItems: "center", gap: "13px" }}>
              <motion.div
                animate={{ y: [0, -3, 0] }}
                transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                style={{
                  width: "44px", height: "44px",
                  borderRadius: "13px",
                  background: isEdit
                    ? "linear-gradient(135deg, #0284c7, #0ea5e9)"
                    : "linear-gradient(135deg, #0ea5e9, #6366f1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 5px 18px rgba(14,165,233,0.45), inset 0 1px 0 rgba(255,255,255,0.15)",
                  flexShrink: 0,
                }}
              >
                {isEdit
                  ? <Building2 style={{ width: "18px", height: "18px", color: "#fff" }} />
                  : <Sparkles style={{ width: "18px", height: "18px", color: "#fff" }} />
                }
              </motion.div>
              <div>
                <h2 style={{ fontSize: "16px", fontWeight: 800, color: "#e2f4ff", margin: 0, letterSpacing: "-0.025em", fontFamily: "'Sora', sans-serif" }}>
                  {isEdit ? t("editMerchant") : t("addNewMerchant")}
                </h2>
                <p style={{ fontSize: "11.5px", color: "#334155", margin: "3px 0 0", fontFamily: "'Sora', sans-serif" }}>
                  {isEdit ? t("updateMerchantDetails") : t("createMerchantUnderReseller")}
                </p>
              </div>
            </div>
          </div>

          {/* Tab Bar */}
          <div style={{
            display: "flex",
            gap: "5px",
            padding: "12px 18px 10px",
            borderBottom: "1px solid rgba(255,255,255,0.03)",
            background: "rgba(3,6,14,0.3)",
            flexShrink: 0,
          }}>
            {tabs.map(({ id, label, icon: Icon }) => (
              <motion.button
                key={id}
                className={`tab-btn ${activeTab === id ? "active" : ""}`}
                onClick={() => setActiveTab(id)}
                whileTap={{ scale: 0.97 }}
              >
                <Icon style={{ width: "11px", height: "11px" }} />
                {label}
              </motion.button>
            ))}
          </div>

          {/* Content */}
          <div className="form-scrollable" style={{ flex: 1, padding: "18px 20px", overflowY: "auto" }}>
            <AnimatePresence mode="wait">

              {/* GENERAL TAB */}
              {activeTab === "general" && (
                <motion.div
                  key="general"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  style={{ display: "flex", flexDirection: "column", gap: "14px" }}
                >
                  <div className="grid-2">
                    <ThemedInput
                      label={t("businessNameRequired")}
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder={t("merchantName")}
                      required
                    />
                    <ThemedInput
                      label={t("merchantCode")}
                      value={formData.code}
                      onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
                      placeholder={t("merchantCodePlaceholder")}
                    />
                  </div>

                  <div className="grid-2">
                    <ThemedInput
                      autoComplete="off"
                      label={t("loginEmailRequired")}
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder={t("emailPlaceholder")}
                      disabled={isEdit}
                      hint={isEdit ? t("emailCannotBeChanged") : ""}
                      required
                    />
                    {!isEdit ? (
                      <ThemedInput
                        autoComplete="new-password"
                        label={t("passwordRequired")}
                        type="password"
                        value={formData.password}
                        onChange={(e) => handleChange("password", e.target.value)}
                        placeholder={t("enterPassword")}
                        required
                      />
                    ) : (
                      <ThemedSelect
                        label={t("status")}
                        value={formData.status}
                        onValueChange={(v) => handleChange("status", v)}
                        options={[
                          { value: "ACTIVE", label: t("active") },
                          { value: "INACTIVE", label: t("inactive") },
                          { value: "SUSPENDED", label: t("suspended") },
                        ]}
                      />
                    )}
                  </div>

                  <div className="grid-2">
                    {!isEdit && (
                      <ThemedSelect
                        label={t("status")}
                        value={formData.status}
                        onValueChange={(v) => handleChange("status", v)}
                        options={[
                          { value: "ACTIVE", label: t("active") },
                          { value: "PENDING", label: t("pending") },
                          { value: "SUSPENDED", label: t("suspended") },
                        ]}
                      />
                    )}
                    <ThemedSelect
                      label={t("settlementPeriod")}
                      value={formData.settlement_period}
                      onValueChange={(v) => handleChange("settlement_period", v)}
                      options={[
                        { value: "T0", label: t("t0SameDay") },
                        { value: "T1", label: t("t1NextDay") },
                        { value: "T3", label: t("t3") },
                        { value: "T7", label: t("t7Weekly") },
                      ]}
                    />
                  </div>

                  <div className="section-divider" />

                  <div className="grid-3">
                    <ThemedInput label={t("contactName")} value={formData.contact_name} onChange={(e) => handleChange("contact_name", e.target.value)} />
                    <ThemedInput label={t("contactEmail")} type="email" value={formData.contact_email} onChange={(e) => handleChange("contact_email", e.target.value)} />
                    <ThemedInput label={t("contactPhone")} value={formData.contact_phone} onChange={(e) => handleChange("contact_phone", e.target.value)} />
                  </div>

                  <ThemedInput label={t("callbackUrl")} value={formData.callback_url} onChange={(e) => handleChange("callback_url", e.target.value)} placeholder={t("callbackUrlPlaceholder")} />
                  <ThemedInput label={t("webhookUrl")} value={formData.webhook_url} onChange={(e) => handleChange("webhook_url", e.target.value)} placeholder={t("webhookUrlPlaceholder")} />

                  <div>
                    <label className="label-sm">{t("notes")}</label>
                    <textarea
                      className="themed"
                      value={formData.notes}
                      onChange={(e) => handleChange("notes", e.target.value)}
                      rows={3}
                    />
                  </div>
                </motion.div>
              )}

              {/* SETTINGS TAB */}
              {activeTab === "settings" && (
                <motion.div
                  key="settings"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  style={{ display: "flex", flexDirection: "column", gap: "14px" }}
                >
                  {/* Commission info banner */}
                  {!isEdit && resellerProfile && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      style={{
                        padding: "15px",
                        background: "rgba(6,182,212,0.05)",
                        border: "1px solid rgba(6,182,212,0.18)",
                        borderRadius: "14px",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      <div style={{
                        position: "absolute", top: 0, left: "15%", right: "15%", height: "1px",
                        background: "linear-gradient(90deg, transparent, rgba(6,182,212,0.3), transparent)",
                      }} />
                      <div style={{ display: "flex", gap: "11px" }}>
                        <div style={{
                          width: "34px", height: "34px", borderRadius: "9px",
                          background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.2)",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                        }}>
                          <Info style={{ width: "14px", height: "14px", color: "#06b6d4" }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ fontSize: "12px", fontWeight: 700, color: "#06b6d4", margin: "0 0 10px", fontFamily: "'Sora', sans-serif" }}>
                            {resellerProfile.parentName
                              ? t("givenBy").replace("{type}", resellerProfile.parentType).replace("{name}", resellerProfile.parentName)
                              : t("yourCommissionRates")}
                          </h4>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
                            {[
                              { label: t("depositCommission"), value: resellerProfile.depositCommission },
                              { label: t("withdrawalCommission"), value: resellerProfile.withdrawalCommission },
                              { label: t("percentageFee"), value: resellerProfile.percentageFee },
                            ].map(({ label, value }) => (
                              <div key={label} style={{
                                padding: "9px 11px",
                                background: "rgba(6,182,212,0.06)",
                                borderRadius: "9px",
                                border: "1px solid rgba(6,182,212,0.1)",
                              }}>
                                <p style={{ fontSize: "9px", color: "#334155", fontWeight: 600, margin: "0 0 3px", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "'Sora', sans-serif" }}>{label}</p>
                                <p style={{ fontSize: "17px", fontWeight: 800, color: "#e2f4ff", margin: 0, letterSpacing: "-0.03em", fontFamily: "'Sora', sans-serif" }}>
                                  {value != null ? `${value}%` : "N/A"}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                    <ToggleCard
                      label={t("enableDeposits")}
                      description={t("allowDepositTransactions")}
                      checked={formData.deposit_enabled}
                      onChange={(v) => handleChange("deposit_enabled", v)}
                    />
                    <ToggleCard
                      label={t("enableWithdrawals")}
                      description={t("allowWithdrawalTransactions")}
                      checked={formData.withdrawal_enabled}
                      onChange={(v) => handleChange("withdrawal_enabled", v)}
                    />
                  </div>

                  <div className="section-divider" />

                  <div>
                    <label className="label-sm">Deposit Limits (฿)</label>
                    <div className="grid-2">
                      <ThemedInput
                        label={t("minDepositThb")}
                        type="number"
                        value={formData.min_deposit}
                        onChange={(e) => handleChange("min_deposit", parseFloat(e.target.value) || 0)}
                      />
                      <ThemedInput
                        label={t("maxDepositThb")}
                        type="number"
                        value={formData.max_deposit}
                        onChange={(e) => handleChange("max_deposit", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label-sm">Withdrawal Limits (฿)</label>
                    <div className="grid-2">
                      <ThemedInput
                        label={t("minWithdrawalThb")}
                        type="number"
                        value={formData.min_withdrawal}
                        onChange={(e) => handleChange("min_withdrawal", parseFloat(e.target.value) || 0)}
                      />
                      <ThemedInput
                        label={t("maxWithdrawalThb")}
                        type="number"
                        value={formData.max_withdrawal}
                        onChange={(e) => handleChange("max_withdrawal", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>

                  <div className="section-divider" />

                  <div style={{
                    padding: "13px 15px",
                    borderRadius: "12px",
                    background: "rgba(6,12,26,0.7)",
                    border: "1px solid rgba(255,255,255,0.05)",
                    display: "flex", alignItems: "center", gap: "11px",
                  }}>
                    <div style={{ width: "34px", height: "34px", borderRadius: "9px", background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Lock style={{ width: "13px", height: "13px", color: "#818cf8" }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: "12.5px", fontWeight: 700, color: "#c8dff0", margin: 0, fontFamily: "'Sora', sans-serif" }}>
                        {t("feeValue")} — <span style={{ color: "#06b6d4" }}>{formData.fee_value}%</span>
                      </p>
                      <p style={{ fontSize: "11px", color: "#2a3a52", margin: "3px 0 0", fontFamily: "'Sora', sans-serif" }}>
                        {t("feeValueSetToBy")?.replace("{value}", formData.fee_value)?.replace("{name}", resellerProfile?.parentName || t("parent")) || `Set by parent reseller`}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* API TAB */}
              {activeTab === "api" && (
                <motion.div
                  key="api"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  style={{ display: "flex", flexDirection: "column", gap: "16px" }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    style={{
                      padding: "13px 15px",
                      background: "rgba(245,158,11,0.06)",
                      border: "1px solid rgba(245,158,11,0.2)",
                      borderRadius: "12px",
                      display: "flex", gap: "11px", alignItems: "flex-start",
                    }}
                  >
                    <AlertCircle style={{ width: "14px", height: "14px", color: "#f59e0b", flexShrink: 0, marginTop: "1px" }} />
                    <p style={{ fontSize: "12px", color: "#92600a", margin: 0, lineHeight: 1.55, fontFamily: "'Sora', sans-serif" }}>
                      {t("apiKeysAutoGenerated")}
                    </p>
                  </motion.div>

                  {/* API Key */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                    <label className="label-sm">{t("apiKeyPublic")}</label>
                    <div style={{ display: "flex", gap: "7px", alignItems: "center" }}>
                      <div className="key-box">
                        {showApiKey ? formData.api_key : "pk_live_" + "•".repeat(32)}
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.93 }}
                        className="icon-btn"
                        style={{ width: "34px", height: "34px", borderRadius: "9px", border: "1px solid rgba(6,182,212,0.15)", background: "rgba(6,182,212,0.05)", color: "#334155", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, outline: "none", transition: "all 0.2s" }}
                        onClick={() => setShowApiKey(!showApiKey)}
                      >
                        {showApiKey ? <EyeOff style={{ width: "13px", height: "13px" }} /> : <Eye style={{ width: "13px", height: "13px" }} />}
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.93 }}
                        style={{ width: "34px", height: "34px", borderRadius: "9px", border: "1px solid rgba(6,182,212,0.15)", background: "rgba(6,182,212,0.05)", color: "#334155", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, outline: "none", transition: "all 0.2s" }}
                        onClick={() => copyToClipboard(formData.api_key, "api")}
                      >
                        <AnimatePresence mode="wait">
                          {copied === "api"
                            ? <motion.div key="check" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}><CheckCircle2 style={{ width: "13px", height: "13px", color: "#34d399" }} /></motion.div>
                            : <motion.div key="copy" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}><Copy style={{ width: "13px", height: "13px" }} /></motion.div>
                          }
                        </AnimatePresence>
                      </motion.button>
                    </div>
                  </div>

                  {/* Secret Key */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                    <label className="label-sm">{t("secretKey")}</label>
                    <div style={{ display: "flex", gap: "7px", alignItems: "center" }}>
                      <div className="key-box">
                        {showSecretKey ? formData.secret_key : "sk_live_" + "•".repeat(40)}
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.93 }}
                        style={{ width: "34px", height: "34px", borderRadius: "9px", border: "1px solid rgba(6,182,212,0.15)", background: "rgba(6,182,212,0.05)", color: "#334155", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, outline: "none", transition: "all 0.2s" }}
                        onClick={() => setShowSecretKey(!showSecretKey)}
                      >
                        {showSecretKey ? <EyeOff style={{ width: "13px", height: "13px" }} /> : <Eye style={{ width: "13px", height: "13px" }} />}
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.93 }}
                        style={{ width: "34px", height: "34px", borderRadius: "9px", border: "1px solid rgba(6,182,212,0.15)", background: "rgba(6,182,212,0.05)", color: "#334155", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, outline: "none", transition: "all 0.2s" }}
                        onClick={() => copyToClipboard(formData.secret_key, "secret")}
                      >
                        <AnimatePresence mode="wait">
                          {copied === "secret"
                            ? <motion.div key="check" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}><CheckCircle2 style={{ width: "13px", height: "13px", color: "#34d399" }} /></motion.div>
                            : <motion.div key="copy" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}><Copy style={{ width: "13px", height: "13px" }} /></motion.div>
                          }
                        </AnimatePresence>
                      </motion.button>
                    </div>
                  </div>

                  <div style={{
                    padding: "13px 15px",
                    borderRadius: "12px",
                    background: "rgba(6,182,212,0.04)",
                    border: "1px solid rgba(6,182,212,0.1)",
                    display: "flex", gap: "11px",
                  }}>
                    <Key style={{ width: "13px", height: "13px", color: "#06b6d4", flexShrink: 0, marginTop: "1px" }} />
                    <p style={{ fontSize: "11.5px", color: "#2a3a52", margin: 0, lineHeight: 1.6, fontFamily: "'Sora', sans-serif" }}>
                      Keep your secret key confidential. Never expose it in client-side code or public repositories. Rotate keys if you suspect compromise.
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer Actions */}
          <div style={{
            padding: "14px 18px",
            borderTop: "1px solid rgba(255,255,255,0.04)",
            background: "rgba(3,6,14,0.5)",
            backdropFilter: "blur(12px)",
            display: "flex",
            justifyContent: "flex-end",
            gap: "9px",
            flexShrink: 0,
          }}>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => onClose(false)}
              style={{
                padding: "9px 17px",
                borderRadius: "10px",
                border: "1px solid rgba(255,255,255,0.08)",
                background: "transparent",
                color: "#475569",
                fontSize: "12.5px",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
              }}
            >
              {t("cancel")}
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.03, boxShadow: "0 6px 22px rgba(6,182,212,0.28)" }}
              whileTap={{ scale: 0.97 }}
              onClick={handleSubmit}
              disabled={saving || !formData.name || (!isEdit && (!formData.email || !formData.password))}
              style={{
                padding: "9px 20px",
                borderRadius: "10px",
                border: "1px solid rgba(6,182,212,0.35)",
                background: saving
                  ? "rgba(6,182,212,0.08)"
                  : "linear-gradient(135deg, rgba(6,182,212,0.8) 0%, rgba(99,102,241,0.75) 100%)",
                color: saving ? "#334155" : "#fff",
                fontSize: "12.5px",
                fontWeight: 700,
                cursor: saving ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: "7px",
                fontFamily: "'Sora', sans-serif",
                boxShadow: "0 3px 12px rgba(6,182,212,0.18)",
                transition: "all 0.25s ease",
              }}
            >
              {saving ? (
                <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.7, ease: "linear" }}>
                  <Save style={{ width: "13px", height: "13px" }} />
                </motion.div>
              ) : (
                <Save style={{ width: "13px", height: "13px" }} />
              )}
              {saving ? t("saving") : isEdit ? t("update") : t("create")}
            </motion.button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}